// ── Third-Person Weapon Attachment ──
// Simplified procedural weapon meshes attached to player bodies in TPP view.
// Uses box geometry for performance (many players visible simultaneously).

import * as THREE from 'three';
import { WEAPONS, type WeaponId } from '../core/config';
import type { WeaponSkinLoader } from '../cosmetics/weapon-skin-loader';
import { weaponSkinRegistry } from '../cosmetics/weapon-skin-registry';
import { StickerRenderer } from '../cosmetics/sticker-renderer';
import { STICKER_MANIFEST } from '../cosmetics/sticker-types';
import type { StickerInstance, NameTag } from '../cosmetics/sticker-types';
import { loadWeaponWorldModel } from './weapon-world-models';

/**
 * C4b: Shared sticker renderer for all TPP weapons in the match. One instance
 * is enough for many bodies because each call operates on a different group.
 */
const tppStickerRenderer = new StickerRenderer();

/** Cache loaded weapon geometries to avoid recreating identical meshes. */
const weaponMeshCache = new Map<WeaponId, THREE.Group>();

const TPP_WEAPON_HOLDER_NAME = '__tppWeapon';
const TPP_BOMB_HOLDER_NAME = '__tppBomb';
let nextTPPWeaponSkinRequestId = 1;
// F-GP-015 (audits/2026-05-07): module-level monotonically-increasing GLB
// request id. Previously the per-holder counter started at 0 on first
// `attachTPPWeapon` and was incremented to 1 before the first dispatch,
// meaning the FIRST GLB load on a brand-new holder couldn't be cancelled
// (the resolved promise's `requestId` was always 1 with nothing to compare
// against). Routing every load through this counter guarantees that
// every dispatched promise carries a unique id even across holders.
let nextTPPWeaponGlbReqId = 1;

/** Shared materials — allocated once, reused across all weapon meshes. */
const gunMaterial = new THREE.MeshStandardMaterial({
  color: 0x333333,
  metalness: 0.8,
  roughness: 0.3,
});

const bladeMaterial = new THREE.MeshStandardMaterial({
  color: 0x999999,
  metalness: 0.9,
  roughness: 0.2,
});

/** C4 body + LED material; reused for every bomb carrier in the match. */
const bombBodyMaterial = new THREE.MeshStandardMaterial({
  color: 0x3b2a12, // dark canvas / explosive compound wrap
  metalness: 0.1,
  roughness: 0.85,
});

const bombLedMaterial = new THREE.MeshStandardMaterial({
  color: 0xff2020,
  emissive: 0xff2020,
  emissiveIntensity: 1.6,
  metalness: 0.0,
  roughness: 0.4,
});

export interface TPPWeaponAppearance {
  skinId?: number;
  wearFloat?: number;
  skinLoader?: WeaponSkinLoader | null;
  /** C4b: optional equipped stickers (5 slots) — applied after skin textures. */
  stickers?: (StickerInstance | null)[];
  /** C4b: optional name tag — applied after skin textures. */
  nameTag?: NameTag;
}

// F-GP-016 (audits/2026-05-07): `getTPPStickerRenderer` removed —
// `tppStickerRenderer.update()` was never driven from any frame loop, so
// the export only existed for tests. The shared instance is still used
// internally by `applyTPPWeaponSkin` when a sticker pipeline runs against
// a TPP weapon mesh; nothing outside this file consumed the helper.

function deepCloneWeaponGroup(source: THREE.Group): THREE.Group {
  const clone = source.clone(true);
  const sourceMeshes: THREE.Mesh[] = [];
  const cloneMeshes: THREE.Mesh[] = [];

  source.traverse((child) => {
    if (child instanceof THREE.Mesh) sourceMeshes.push(child);
  });
  clone.traverse((child) => {
    if (child instanceof THREE.Mesh) cloneMeshes.push(child);
  });

  for (let i = 0; i < Math.min(sourceMeshes.length, cloneMeshes.length); i++) {
    cloneMeshes[i].geometry = sourceMeshes[i].geometry.clone();
  }

  return clone;
}

function disposeMeshMaterial(material: THREE.Material | THREE.Material[]): void {
  const materials = Array.isArray(material) ? material : [material];
  for (const mat of materials) {
    if (mat === gunMaterial || mat === bladeMaterial) continue;
    mat.dispose();
  }
}

function disposeWeaponObject(root: THREE.Object3D): void {
  root.traverse((obj) => {
    if (!(obj instanceof THREE.Mesh)) return;
    obj.geometry.dispose();
    disposeMeshMaterial(obj.material);
  });
}

async function applyTPPWeaponSkin(
  weaponMesh: THREE.Group,
  weaponId: WeaponId,
  appearance?: TPPWeaponAppearance,
): Promise<void> {
  const skinId = appearance?.skinId ?? 0;
  const skinLoader = appearance?.skinLoader;

  const requestId = nextTPPWeaponSkinRequestId++;
  weaponMesh.userData.tppWeaponSkinRequestId = requestId;

  // Skin textures (skipped when no loader / default skin).
  if (skinLoader && skinId > 0) {
    const skin = weaponSkinRegistry.getSkin(skinId);
    if (skin && skin.weaponIds.includes(weaponId)) {
      try {
        const textures = await skinLoader.loadSkinTextures(skin);
        if (!weaponMesh.parent) return;
        if (weaponMesh.userData.tppWeaponSkinRequestId !== requestId) return;
        skinLoader.applySkinToMesh(weaponMesh, textures, skin, appearance?.wearFloat ?? 0);
      } catch (err) {
        console.warn(`[TPP] Failed to apply skin ${skinId} to ${weaponId}:`, err);
      }
    }
  }

  // C4b: project equipped stickers/name tag onto the TPP weapon mesh so
  // teammates / spectators see what the wearer has equipped.
  if (!weaponMesh.parent) return;
  if (weaponMesh.userData.tppWeaponSkinRequestId !== requestId) return;
  const stickers = appearance?.stickers;
  if (stickers && stickers.length > 0) {
    try {
      await tppStickerRenderer.applyStickers(weaponMesh, weaponId, stickers, STICKER_MANIFEST);
    } catch (err) {
      console.warn(`[TPP] Failed to apply stickers to ${weaponId}:`, err);
    }
  }
  if (appearance?.nameTag) {
    tppStickerRenderer.applyNameTag(weaponMesh, appearance.nameTag);
  }
}

/**
 * Create a simple procedural weapon mesh for third-person view.
 * These are simplified box shapes — NOT the detailed viewmodel GLBs.
 * Procedural geometry keeps draw calls low when many players are visible.
 */
function createTPPWeaponMesh(weaponId: WeaponId): THREE.Group {
  // Return cached clone if available
  const cached = weaponMeshCache.get(weaponId);
  if (cached) return deepCloneWeaponGroup(cached);

  const cfg = WEAPONS[weaponId];
  const group = new THREE.Group();
  group.name = `tpp_weapon_${weaponId}`;

  switch (cfg.type) {
    case 'pistol': {
      // Small, short handgun
      const body = new THREE.Mesh(
        new THREE.BoxGeometry(0.06, 0.12, 0.20),
        gunMaterial,
      );
      body.castShadow = true;
      body.receiveShadow = true;
      group.add(body);
      break;
    }

    case 'rifle':
    case 'smg': {
      // Long body + small magazine box
      const body = new THREE.Mesh(
        new THREE.BoxGeometry(0.05, 0.08, 0.65),
        gunMaterial,
      );
      body.castShadow = true;
      body.receiveShadow = true;
      group.add(body);

      // Magazine
      const mag = new THREE.Mesh(
        new THREE.BoxGeometry(0.04, 0.10, 0.06),
        gunMaterial,
      );
      mag.position.set(0, -0.08, 0.10);
      mag.castShadow = true;
      mag.receiveShadow = true;
      group.add(mag);
      break;
    }

    case 'sniper': {
      // Very long barrel
      const body = new THREE.Mesh(
        new THREE.BoxGeometry(0.04, 0.07, 0.85),
        gunMaterial,
      );
      body.castShadow = true;
      body.receiveShadow = true;
      group.add(body);
      break;
    }

    case 'shotgun': {
      // Stocky, medium length
      const body = new THREE.Mesh(
        new THREE.BoxGeometry(0.05, 0.08, 0.60),
        gunMaterial,
      );
      body.castShadow = true;
      body.receiveShadow = true;
      group.add(body);
      break;
    }

    case 'melee': {
      // Small blade
      const blade = new THREE.Mesh(
        new THREE.BoxGeometry(0.02, 0.03, 0.25),
        bladeMaterial,
      );
      blade.castShadow = true;
      blade.receiveShadow = true;
      group.add(blade);
      break;
    }
  }

  // Cache the prototype for future clones
  weaponMeshCache.set(weaponId, deepCloneWeaponGroup(group));
  return group;
}

/**
 * Attach a third-person weapon to a player body group.
 * Finds or creates a child group named '__tppWeapon' on the body.
 * Positions the weapon at the right hand area.
 */
export function attachTPPWeapon(
  bodyGroup: THREE.Group,
  weaponId: WeaponId,
  appearance?: TPPWeaponAppearance,
): void {
  let holder = bodyGroup.getObjectByName(TPP_WEAPON_HOLDER_NAME) as THREE.Group | undefined;

  if (holder) {
    // Remove old weapon mesh children (dispose geometry)
    while (holder.children.length > 0) {
      const child = holder.children[0];
      holder.remove(child);
      disposeWeaponObject(child);
    }
  } else {
    holder = new THREE.Group();
    holder.name = TPP_WEAPON_HOLDER_NAME;
    // Right hand area on the procedural body
    holder.position.set(0.3, 1.15, -0.2);
    bodyGroup.add(holder);
  }

  // W6: render procedural box first so the body is never weaponless during the
  // async GLB fetch. Once the real model arrives, the box is swapped out and
  // the skin/sticker pipeline runs against the GLB mesh.
  const placeholder = createTPPWeaponMesh(weaponId);
  holder.add(placeholder);
  void applyTPPWeaponSkin(placeholder, weaponId, appearance);

  // F-GP-015: pull a fresh id from the module-level counter so even the
  // first attach on a brand-new holder gets a unique tag — eliminates the
  // race where the inaugural GLB load couldn't be cancelled.
  const requestId = nextTPPWeaponGlbReqId++;
  holder.userData.tppGlbReqId = requestId;
  void loadWeaponWorldModel(weaponId).then((glb) => {
    if (!holder) return;
    // The holder's request id changes whenever attachTPPWeapon is re-called
    // (weapon switch, respawn). Stale loads are dropped on the floor.
    if (holder.userData.tppGlbReqId !== requestId || !holder.parent) return;
    if (!glb) return; // GLB missing or load failed — keep the procedural box
    // The shared loader pre-applies the FPV manifest scale (~0.010), which
    // is tuned for camera-relative rendering and produces a sub-10cm weapon
    // on the body — invisible from third-person distance. Re-scale up so
    // the body weapon reads at ~0.6m, matching the procedural placeholder
    // it replaces (mirrors the same rescale used by weapon-pickup.ts).
    glb.scale.multiplyScalar(8);
    glb.position.set(0, 0, 0);
    while (holder.children.length > 0) {
      const child = holder.children[0];
      holder.remove(child);
      disposeWeaponObject(child);
    }
    holder.add(glb);
    void applyTPPWeaponSkin(glb, weaponId, appearance);
  });
}

/**
 * Remove the third-person weapon from a body group.
 * Disposes geometry but not the shared materials.
 */
export function removeTPPWeapon(bodyGroup: THREE.Group): void {
  const holder = bodyGroup.getObjectByName(TPP_WEAPON_HOLDER_NAME);
  if (!holder) return;

  disposeWeaponObject(holder);
  bodyGroup.remove(holder);
}

/**
 * Aim the TPP weapon along the player's look pitch. The holder is parented to
 * the body (which carries yaw), so setting holder.rotation.x = -pitch makes the
 * weapon follow vertical aim. Clamped to prevent unnatural inversion.
 */
export function setTPPWeaponPitch(bodyGroup: THREE.Group, pitch: number): void {
  const holder = bodyGroup.getObjectByName(TPP_WEAPON_HOLDER_NAME);
  if (!holder) return;
  const clamped = Math.max(-1.2, Math.min(1.2, pitch));
  // Three.js camera convention: positive pitch looks up. To make the weapon
  // model follow that, we rotate around local X by -pitch.
  holder.rotation.x = -clamped;
}

/**
 * Attach a C4 prop to the carrier's lower back so opponents can identify the
 * bomb carrier at a glance. Uses a dedicated holder (`__tppBomb`) distinct
 * from the weapon holder so the bomb and primary weapon render together.
 */
export function attachTPPBomb(bodyGroup: THREE.Group): void {
  let holder = bodyGroup.getObjectByName(TPP_BOMB_HOLDER_NAME) as THREE.Group | undefined;
  if (holder) return; // already attached

  holder = new THREE.Group();
  holder.name = TPP_BOMB_HOLDER_NAME;
  // Lower back of the procedural body: slightly below shoulder blades, behind
  // the spine. The character body is roughly 1.8m tall with origin at feet.
  holder.position.set(0, 1.05, 0.18);
  bodyGroup.add(holder);

  const body = new THREE.Mesh(
    new THREE.BoxGeometry(0.2, 0.13, 0.08),
    bombBodyMaterial,
  );
  body.castShadow = true;
  body.receiveShadow = true;
  holder.add(body);

  // Blinking LED — single small cube on the front face; a shader pulse would
  // be overkill for a TPP identifier visible across the map.
  const led = new THREE.Mesh(
    new THREE.BoxGeometry(0.02, 0.02, 0.01),
    bombLedMaterial,
  );
  led.position.set(0.06, 0.03, -0.045);
  holder.add(led);
}

/**
 * Remove the C4 prop from a body group. Safe to call when no bomb is
 * attached. The shared materials are NOT disposed because they're reused
 * across carriers; only the geometries are dropped.
 */
export function removeTPPBomb(bodyGroup: THREE.Group): void {
  const holder = bodyGroup.getObjectByName(TPP_BOMB_HOLDER_NAME);
  if (!holder) return;

  holder.traverse((obj) => {
    if (obj instanceof THREE.Mesh) {
      obj.geometry.dispose();
    }
  });
  bodyGroup.remove(holder);
}

// F-GP-016 (audits/2026-05-07): `getTPPMuzzlePosition` removed — the only
// caller was its own unit test. The actual TPP muzzle flash in
// `client/src/game.ts` reconstructs the position manually from
// `localPlayer.position + 0.3 right + 0.5 forward`, so the helper had no
// production consumer. Re-add only if/when a renderer subsystem actually
// needs the world-space barrel tip.
